import { NotificationsLevel } from "@js2/lib/enums";
import { show } from "@js2/notifications/notifications";
import { CANCEL_ERROR } from "@js2/requests";
import isIP from "validator/es/lib/isIP";
import { ErrorResponse } from "../../requests/@types";
import { refangUrlInText } from "../search/components/utils";
import type {
  DomainReport,
  IPReport,
  ThreatIntelProps,
  UrlReport
} from "./@types";
import { EnrichedFile } from "./@types/common";
import {
  DownloadedFile as DomainDownloadedFile,
  EnrichedDownloadedFile
} from "./@types/Domain";
import { DownloadedFile as IPDownloadedFile } from "./@types/IP";
import { Analysis, NetworkRepEntry } from "./@types/Url";
import {
  MAX_URL_LENGTH,
  NETWORK_TAB_KEYS,
  PREVIEW_DATA_KEYS,
  URL_PREVIEW_DATA_ITEMS,
  VALID_URL_PROTOCOL_REGEX
} from "./consts";
import {
  domainReportCategoriesIssue,
  domainReportDownloadedFilesIssue,
  domainReportLastSeenIssue,
  domainReportThirdPartyRepIssue,
  domainReportTopThreatIssue,
  ipReportCategoriesIssue,
  ipReportDownloadedFilesIssue,
  ipReportThirdPartyRepIssue,
  ipReportTopThreatIssue,
  urlReportCategoriesIssue,
  urlReportClassificationIssue,
  urlReportLastAnalysisIssue,
  urlReportThirdPartyRepIssue,
  urlReportTopThreatIssue
} from "./issuepredicates";

export const getDomainFromUrl = (url: string) => {
  let urlObj;
  try {
    urlObj = new URL(url);
  } catch (e) {
    if (!url.includes("http") && !url.includes("https")) {
      return undefined;
    }
    return undefined;
  }
  return urlObj.hostname;
};

export const getVisibleTabs = ({ url, domain, ip }: ThreatIntelProps) => ({
  [NETWORK_TAB_KEYS.URL_ANALYSIS]: !!url,
  [NETWORK_TAB_KEYS.DOMAIN_ANALYSIS]: !!domain,
  [NETWORK_TAB_KEYS.IP_ANALYSIS]: !!ip
});

export const removeSurroundingQuotes = (str: string) =>
  str.replace(/^"(.*)"$/, "$1");

const extractIPFromDomainReport = (domainReport?: DomainReport) =>
  domainReport?.last_dns_records.find(item => item.type === "A")?.value;

export const extractIPfromNetworkThreatReports = ({
  ipReport,
  urlReport,
  domainReport
}: {
  ipReport?: IPReport | undefined;
  urlReport?: UrlReport | undefined;
  domainReport?: DomainReport | undefined;
}) =>
  ipReport?.requested_ip ||
  urlReport?.analysis?.last_analysis?.serving_ip_address ||
  extractIPFromDomainReport(domainReport) ||
  (isIP(urlReport?.analysis?.last_analysis?.domain || "") &&
    urlReport?.analysis?.last_analysis?.domain) || // ex last_analysis?.domain: http://12.34.4.56 -> domain == ip
  "";

export interface Predicate<T> {
  (ntiReport: T | undefined): number;
}
export const countReportIssues = <T>(
  data: T,
  predicates: Predicate<T>[]
): number =>
  predicates?.reduce((sum, predicate) => sum + predicate(data), 0) ?? 0;

export const getUrlReportIssues = (urlReport: UrlReport | undefined) =>
  countReportIssues(urlReport, [
    urlReportCategoriesIssue,
    urlReportLastAnalysisIssue,
    urlReportTopThreatIssue,
    urlReportThirdPartyRepIssue,
    urlReportClassificationIssue
  ]);

export const getDomainReportIssues = (domainReport: DomainReport | undefined) =>
  countReportIssues(domainReport, [
    domainReportLastSeenIssue,
    domainReportCategoriesIssue,
    domainReportDownloadedFilesIssue,
    domainReportTopThreatIssue,
    domainReportThirdPartyRepIssue
  ]);

export const getIPReportIssues = (ipReport: IPReport | undefined) =>
  countReportIssues(ipReport, [
    ipReportCategoriesIssue,
    ipReportDownloadedFilesIssue,
    ipReportTopThreatIssue,
    ipReportThirdPartyRepIssue
  ]);

const getClassificationFromEntries = (
  entries: NetworkRepEntry[],
  url: string
) => {
  const entry = entries.find(e => e.requested_network_location === url);
  return entry?.classification || "unknown";
};

export const enrichData = (
  files: Array<DomainDownloadedFile | IPDownloadedFile>,
  entries: NetworkRepEntry[]
) =>
  files.map(file => ({
    ...file,
    url_classification: getClassificationFromEntries(
      entries,
      file.last_download_url
    )
  }));

export const analysesWithPreviewData = (analysisHistory?: Analysis[]) => {
  const itemsWithPreviewData =
    analysisHistory?.filter(historyItem =>
      URL_PREVIEW_DATA_ITEMS.some(value => value in historyItem)
    ) ?? [];

  return {
    analysesWithPreview: itemsWithPreviewData,
    hasPreviewData: itemsWithPreviewData.length > 0
  };
};

export const filterLatestScreenshotFromReport = (
  analysisHistory?: Analysis[]
) => {
  const analysesWithScreenshots =
    analysisHistory?.filter(
      historyItem => PREVIEW_DATA_KEYS.SCREENSHOT in historyItem
    ) ?? [];

  const latestScreenshot = analysesWithScreenshots.at(-1)?.screenshot_url;
  return {
    latestScreenshot
  };
};

/**
 * Prepend http:// to the url if it's missing and refang the URL
 */
export const prependHttpToUrl = (url?: string) => {
  if (!url) return "";
  const refangedUrl = refangUrlInText(url);
  const isValidProtocol = refangedUrl.match(VALID_URL_PROTOCOL_REGEX);
  return isValidProtocol ? refangedUrl : `http://${refangedUrl}`;
};

export const getValidationFieldError = (
  data: unknown
): { message: string; fieldName: string } | undefined => {
  const detail = (data as { detail?: { fields: Record<string, string[]> } })
    ?.detail;
  if (!detail?.fields) return undefined;
  const entry = Object.entries(detail.fields).find(
    ([, msgs]) => msgs?.length > 0
  );
  if (!entry) return undefined;
  return { fieldName: entry[0], message: entry[1][0] };
};

/**
 * validateUrl performs client-side validation before submitting a URL for analysis.
 *
 * It checks for:
 * - URL length (must not exceed MAX_URL_LENGTH)
 * - Protocol (only http and https)
 * - Presence of a hostname
 * - Invalid special characters in the hostname
 * - Hostname must contain a domain extension (at least one dot)
 * - Empty labels in the hostname (consecutive dots, leading/trailing dots)
 * - Labels must not start or end with a hyphen
 *
 * Returns an error message string if invalid, or null if valid.
 */

export const validateUrl = (url: string): string | null => {
  if (url.length > MAX_URL_LENGTH) {
    return `Enter a valid URL - URL must not exceed ${MAX_URL_LENGTH} characters.`;
  }

  const INVALID_HOSTNAME_CHARS = [
    " ",
    "!",
    '"',
    "#",
    "$",
    "%",
    "&",
    "'",
    "(",
    ")",
    "*",
    "+",
    ",",
    "/",
    ";",
    "<",
    "=",
    ">",
    "?",
    "@",
    "[",
    "\\",
    "]",
    "^",
    "_",
    "`",
    "{",
    "|",
    "}",
    "~"
  ];

  const protocolMatch = url.match(/^https?:\/\//i);
  if (!protocolMatch) {
    return "Enter a valid URL - only http and https schemes are allowed.";
  }
  let afterProtocol = url.slice(protocolMatch[0].length);

  // Strip userinfo (user:pass@) if present
  const atIndex = afterProtocol.indexOf("@");
  if (atIndex !== -1) {
    afterProtocol = afterProtocol.slice(atIndex + 1);
  }

  // IPv6 addresses in brackets — skip special char validation for those
  const isIPv6 = afterProtocol.startsWith("[");

  let rawHostname: string;
  if (isIPv6) {
    rawHostname = afterProtocol.slice(0, afterProtocol.indexOf("]") + 1);
  } else {
    const authoritySegment = afterProtocol.split(/[:/]/)[0];
    const delimMatch = authoritySegment.match(/[?#]/);
    if (
      delimMatch &&
      authoritySegment.slice(0, delimMatch.index).includes(".")
    ) {
      // Valid hostname before ?/# — treat as query/fragment delimiter
      rawHostname = authoritySegment.slice(0, delimMatch.index);
    } else {
      // No valid hostname before ?/# — check the whole segment for invalid chars
      rawHostname = authoritySegment;
    }
  }

  if (!rawHostname) {
    return "Enter a valid URL - no hostname found.";
  }

  if (!isIPv6) {
    for (const char of INVALID_HOSTNAME_CHARS) {
      if (rawHostname.includes(char)) {
        return `Enter a valid URL - hostname contains a "${char}", which is not allowed.`;
      }
    }

    if (!rawHostname.includes(".")) {
      return "Enter a valid URL - hostname must contain a domain extension.";
    }

    const labels = rawHostname.endsWith(".")
      ? rawHostname.slice(0, -1).split(".")
      : rawHostname.split(".");
    for (const label of labels) {
      if (!label) {
        return "Enter a valid URL - hostname contains empty labels.";
      }
      if (label.startsWith("-") || label.endsWith("-")) {
        return `Enter a valid URL - hostname "${label}" must not start or end with a hyphen.`;
      }
    }
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return "Enter a valid URL.";
  }

  const { hostname } = parsed;

  if (!hostname) {
    return "Enter a valid hostname.";
  }

  return null;
};

const HTTP_STATUS_CLASS: Record<number, string> = {
  1: "INFO",
  2: "OK",
  3: "REDIRECT",
  4: "CLIENT ERROR",
  5: "SERVER ERROR"
};

export const getHttpStatusText = (code?: number): string | null =>
  code != null ? HTTP_STATUS_CLASS[Math.floor(code / 100)] ?? null : null;

export const getRelevantReportError = (
  url: string,
  domain: string,
  ip: string,
  errors: {
    url: ErrorResponse | null;
    domain: ErrorResponse | null;
    ip: ErrorResponse | null;
  }
): ErrorResponse | null => {
  if (url) return errors.url;
  if (domain) return errors.domain;
  if (ip) return errors.ip;
  return null;
};

export const mapToEnrichedFile = (
  file: EnrichedDownloadedFile
): EnrichedFile => ({
  sha1: file.sha1,
  filename: null,
  last_seen: file.last_seen ?? "",
  sample_type: file.sample_type ?? null,
  file_size: file.sample_size,
  threat_name: file.threat_name ?? null,
  classification: file.classification ?? "",
  factor: Number(file.risk_score) || 0,
  file_count: 0,
  processing_status: null,
  submission_users: [],
  submission_count: null,
  downloadable: file.sample_available,
  scanner_count: file.scanner_count,
  scanner_match: file.scanner_match,
  location: "cloud"
});

export const handleQueryError = (e: ErrorResponse, context: string) => {
  if (e.problem === CANCEL_ERROR) return undefined;

  show({
    level: NotificationsLevel.ERROR,
    message: `Error encountered while fetching ${context}.`
  });
  throw e;
};
